/**
 * mix_math.ts — levels, ducking and the environment snapshot (2026-09-20). PURE.
 *
 * DUCKING IS ARBITRATED, NEVER STACKED. Several things may ask a bus to make
 * room at once (a versona speaking, a cinematic line, a live voice-chat peer).
 * Adding their attenuations makes the game disappear; the rule here is the
 * STRONGEST request per bus, capped — and a request is a TOKEN that ends on its
 * own clock, so a cancelled utterance can never leave the music ducked.
 *
 * USER GAIN, SNAPSHOT GAIN, DUCK GAIN AND THE MUTE GATE ARE SEPARATE STAGES.
 * One shared gain would let a cutscene exit or a duck release overwrite the
 * volume a player chose.
 */
import type { Bus } from './buses.js';

export const dbToGain = (db: number): number => (db <= -120 ? 0 : Math.pow(10, db / 20));
export const gainToDb = (g: number): number => (g <= 1e-6 ? -120 : 20 * Math.log10(g));

/** A slider's 0..1 → gain. Squared: the top half of a linear fader is nearly inaudible change. */
export function volumeToGain(v: number): number { const c = Math.min(1, Math.max(0, v)); return c * c; }

export type DuckProfile = Partial<Record<Bus, number>>; // bus → attenuation in dB (negative)

/** What SPEECH asks of the mix (the report's proposed starting ranges, conservative end). */
export const DUCK_SPEECH: DuckProfile = { music: -7, ambience: -4, weather: -4, combat: -2, vehicles: -3, machinery: -2 };
/** A live voice-chat peer talking: lighter — a call must not pump the world on every syllable. */
export const DUCK_VOICECHAT: DuckProfile = { music: -4, ambience: -2, weather: -2 };
/** Never duck a bus further than this, whatever asks. */
export const DUCK_FLOOR_DB = -12;
export const DUCK_ATTACK_S = 0.06;
export const DUCK_RELEASE_S = 0.5;

export interface DuckRequest { tag: string; profile: DuckProfile; untilS: number }

/** The gain each bus should sit at right now: the strongest live request per bus, floored. */
export function duckGains(requests: Iterable<DuckRequest>, nowS: number): Partial<Record<Bus, number>> {
  const db: Partial<Record<Bus, number>> = {};
  for (const r of requests) {
    if (r.untilS <= nowS) continue;
    for (const [bus, att] of Object.entries(r.profile) as Array<[Bus, number]>) {
      const cur = db[bus] ?? 0;
      if (att < cur) db[bus] = Math.max(DUCK_FLOOR_DB, att);
    }
  }
  const out: Partial<Record<Bus, number>> = {};
  for (const [bus, d] of Object.entries(db) as Array<[Bus, number]>) out[bus] = dbToGain(d);
  return out;
}

// ─── The environment snapshot ────────────────────────────────────────────────

export type ReverbKind = 'none' | 'room' | 'cave' | 'hall';

export interface EnvInput {
  submerged: boolean; indoors: boolean; inCave: boolean; spaceFlight: boolean; aboardShip: boolean;
  /** The room's acoustic family (`ROOM_PRESETS`) — absent or unknown = the one generic room (interiors.md law 26). */
  room?: string | null;
  /** 0 at the doorway … 1 deep in the room (`doorBlendFor`): the street leaks in at the door. Ignored without `room`. */
  doorBlend?: number;
}

export interface EnvSnapshot {
  /** Low-pass over the WORLD sub-mix (Hz). Under water everything physical is dull; warnings and speech are not in this mix. */
  worldCutoffHz: number;
  /** Low-pass over the weather bus: rain heard through a roof loses its hiss, not just its level. */
  weatherCutoffHz: number;
  reverb: ReverbKind;
  /** Wet return level (linear). Conservative — a tail should be noticed when it is gone, not when it is there. */
  reverbWet: number;
  /** Which impulse rings when it is not the kind's own (`room:<family>`). Absent = `REVERB_RECIPES[reverb]`. */
  recipe?: string;
}

export function envSnapshot(e: EnvInput): EnvSnapshot {
  if (e.submerged) return { worldCutoffHz: 650, weatherCutoffHz: 400, reverb: 'none', reverbWet: 0 };
  if (e.spaceFlight) return { worldCutoffHz: 20000, weatherCutoffHz: 20000, reverb: 'none', reverbWet: 0 };
  if (e.inCave) return { worldCutoffHz: 20000, weatherCutoffHz: 900, reverb: 'cave', reverbWet: 0.34 };
  if (e.aboardShip) return { worldCutoffHz: 20000, weatherCutoffHz: 700, reverb: 'hall', reverbWet: 0.26 };
  if (e.indoors) {
    const p = e.room ? ROOM_PRESETS[e.room as RoomPresetKey] : undefined;
    // THE FALLBACK (interiors.md law 26): no family, an unknown one, the look's lever off → the one generic room.
    if (!p) return { worldCutoffHz: 20000, weatherCutoffHz: 1400, reverb: 'room', reverbWet: 0.22 };
    const b = quantBlend(e.doorBlend ?? 1);
    return { worldCutoffHz: 20000, weatherCutoffHz: doorCutoffHz(p.weatherCutoffHz, b), reverb: 'room', reverbWet: +(p.wet * (DOOR_WET_FLOOR + (1 - DOOR_WET_FLOOR) * b)).toFixed(3), recipe: `room:${e.room}` };
  }
  return { worldCutoffHz: 20000, weatherCutoffHz: 20000, reverb: 'none', reverbWet: 0 };
}

/** Impulse-response recipes (procedural — no asset): seconds of tail, damping of the highs, pre-delay. */
export const REVERB_RECIPES: Record<Exclude<ReverbKind, 'none'>, { seconds: number; damping: number; preDelayMs: number; density: number }> = {
  room: { seconds: 0.55, damping: 0.55, preDelayMs: 6, density: 1 },
  hall: { seconds: 1.3, damping: 0.4, preDelayMs: 14, density: 0.9 },
  cave: { seconds: 2.2, damping: 0.3, preDelayMs: 24, density: 0.75 },
};

// ─── The room, by purpose and volume (interiors programme, slice D, 2026-10-01) ──────────────────────────────

/**
 * A room's acoustic FAMILY (chosen by `lib/interior_style.ts roomFamilyFor` from the purpose, the builder and the
 * shell's volume). Each is an impulse RECIPE for the ONE convolver (synthesised like every room — nothing to
 * download), a wet level, how much of the weather's hiss the walls keep out, and a quiet NON-VOCAL bed
 * (`ambient_room_<family>`, synthesised offline — `tools/asset-pipeline/synth-room-beds.mjs`): ventilation, a mains
 * hum, a stove's tick. No voices, murmurs, footsteps or cries — an empty room has no crowd (sound.md law 5).
 * A reverb preset is a character, not a simulation: it does not find walls or occlude anything.
 */
export type RoomPresetKey = 'home' | 'bay' | 'hall' | 'clinic' | 'bank' | 'factory' | 'civic';
export interface RoomPreset {
  seconds: number; damping: number; preDelayMs: number; density: number;
  wet: number; weatherCutoffHz: number;
  bed: string; bedGain: number;
}
export const ROOM_PRESETS: Readonly<Record<RoomPresetKey, RoomPreset>> = {
  /** A soft home: cloth and wood soak it up — short, dark, close. */
  home: { seconds: 0.36, damping: 0.8, preDelayMs: 4, density: 1, wet: 0.14, weatherCutoffHz: 1200, bed: 'ambient_room_home', bedGain: 0.11 },
  /** A damped Borean bay: thick insulated walls — short and very dark; the weather is far away. */
  bay: { seconds: 0.48, damping: 0.85, preDelayMs: 5, density: 1, wet: 0.16, weatherCutoffHz: 950, bed: 'ambient_room_bay', bedGain: 0.12 },
  /** An open Aurelian hall: tall, airy, a longer bright tail. */
  hall: { seconds: 0.9, damping: 0.45, preDelayMs: 10, density: 0.95, wet: 0.21, weatherCutoffHz: 1600, bed: 'ambient_room_hall', bedGain: 0.1 },
  /** A clinic: wipe-clean and quiet — a short clear room under steady ventilation. */
  clinic: { seconds: 0.44, damping: 0.6, preDelayMs: 5, density: 1, wet: 0.15, weatherCutoffHz: 1300, bed: 'ambient_room_clinic', bedGain: 0.13 },
  /** A hard bank hall: stone and sealed alloy — a long bright tail, a long pre-delay. */
  bank: { seconds: 1.2, damping: 0.3, preDelayMs: 12, density: 0.9, wet: 0.24, weatherCutoffHz: 1100, bed: 'ambient_room_bank', bedGain: 0.09 },
  /** A factory floor: hard, wide, sparse early reflections; building services hum (never a production line that is not there). */
  factory: { seconds: 0.8, damping: 0.35, preDelayMs: 8, density: 0.85, wet: 0.2, weatherCutoffHz: 1500, bed: 'ambient_room_factory', bedGain: 0.14 },
  /** A large civic hall: the longest room tail in the game short of a cave. */
  civic: { seconds: 1.6, damping: 0.35, preDelayMs: 18, density: 0.9, wet: 0.26, weatherCutoffHz: 1200, bed: 'ambient_room_civic', bedGain: 0.09 },
};
export const ROOM_PRESET_KEYS = Object.keys(ROOM_PRESETS) as RoomPresetKey[];

/** The doorway is open: within this many metres of it the street leaks in and the room has not closed round you yet. */
export const DOOR_BLEND_M = 4;
/** The share of a room's wet level heard standing IN its doorway. */
export const DOOR_WET_FLOOR = 0.45;
/** The weather's shelter cutoff standing in the doorway (rain through an open door keeps most of its hiss). */
export const DOOR_WEATHER_HZ = 5200;
/** Steps of the blend: a walk from the door is at most four snapshot changes, never one per frame. */
const quantBlend = (b: number): number => Math.round(Math.min(1, Math.max(0, b)) * 4) / 4;
/** 0 in the doorway … 1 at `DOOR_BLEND_M` inside (quantised). */
export function doorBlendFor(distToDoorM: number): number { return quantBlend(distToDoorM / DOOR_BLEND_M); }
/** The weather cutoff between the doorway and the room (a log blend — a cutoff is heard in octaves). */
export function doorCutoffHz(roomHz: number, blend: number): number { return Math.round(Math.exp(Math.log(DOOR_WEATHER_HZ) + (Math.log(roomHz) - Math.log(DOOR_WEATHER_HZ)) * blend)); }
/** The bed's level: full deep in the room, a third in the doorway. */
export function roomBedGain(p: RoomPreset, blend: number): number { return +(p.bedGain * (0.35 + 0.65 * quantBlend(blend))).toFixed(4); }
/** The impulse recipe a snapshot rings (null = dry). */
export function reverbRecipeOf(e: EnvSnapshot): { key: string; seconds: number; damping: number; preDelayMs: number; density: number } | null {
  if (e.reverb === 'none') return null;
  const fam = e.recipe?.startsWith('room:') ? ROOM_PRESETS[e.recipe.slice(5) as RoomPresetKey] : undefined;
  const r = fam ?? REVERB_RECIPES[e.reverb];
  return { key: fam ? e.recipe! : e.reverb, seconds: r.seconds, damping: r.damping, preDelayMs: r.preDelayMs, density: r.density };
}
